import { apiGatewayEndpoints } from '@/services/arcane-api-gateway/api-gateway-endpoints';
import { isEmpty } from 'lodash';
import { identifyUser, resetUser } from '~/composables/usePostHog';

let _arcaneApiGateway = null;
export const setArcaneApiGateway = (instance) => {
  _arcaneApiGateway = instance;
};
import {
  ChangeEmail,
  ChangePasswordRules,
  ChangeSignUpPassword,
  SetActiveOrganization,
  SetUserData,
  SetOrgEnsuredState,
  ResetStore,
  SetLastOrganizationId,
  SetPlanInfo,
  SetSubscriptions,
} from '~/constants/mutations';
import { endpoints } from '~/services/arcane-api/endpoints';
import {
  FetchOrganization,
  SendResetPassword,
  FetchUserData,
  SignUp,
  UpdateUserData,
  ChangePassword,
  OrgEnsured,
  ClearStore,
  Logout,
  UpdateOrganization,
  ChangeActiveOrganization,
} from '~/constants/actions';
import {
  GetOrganizationId,
  GetActiveOrganization,
  GetUserData,
  GetLastOrganizationId,
  GetActiveOrganizationRole,
  GetOrganizations,
  GetOrganizationName,
  GetOrganizationById,
  HasUnlimitedAccess,
  GetMainSubscription,
} from '~/constants/getters';

import ArcaneApi from '~/services/arcane-api';

import auth0Client from '~/plugins/authClient';

const defaultState = () => {
  return {
    signUpPassword: '',
    confirmSignUpPassword: '',
    email: '',
    organizationId: '',
    organizationName: '',
    userData: null,
    url: import.meta.env.BASE_API_URL,
    orgEnsuredState: false,
    lastOrgId: {},
    planInfo: null,
    plans: [],
    subscriptions: [],
  };
};

const state = defaultState;

const mutations = {
  [ResetStore]: (state) => {
    Object.assign(state, {
      ...defaultState(),
      lastOrgId: state.lastOrgId,
    });
  },

  [ChangeSignUpPassword]: (state, data) => {
    state.signUpPassword = data;
  },

  [ChangePasswordRules]: (state, data) => {
    state.passwordRules = data;
  },

  [ChangeEmail]: (state, email) => {
    state.email = email;
  },

  [SetUserData](state, data) {
    state.userData = { ...state.userData, ...data };
  },

  [SetActiveOrganization](state, data) {
    state.organizationId = data;
  },

  [SetOrgEnsuredState](state, currentState) {
    state.orgEnsuredState = currentState;
  },

  [SetLastOrganizationId](state, organizationId) {
    state.lastOrgId[state.userData.id] = organizationId;
  },

  [SetPlanInfo](state, quota) {
    state.planInfo = quota || {};
  },

  [SetSubscriptions](state, subscriptions) {
    state.subscriptions = subscriptions || [];
  },
};

/**
 * Important note, we don't use arrow functions here
 * because that would override @var this context and
 * @var this.$axios would result as an undefined variable.
 */
const actions = {
  [ClearStore]({ commit }) {
    commit(ResetStore);
  },

  /**
   * USER
   */

  setUserData({ commit }, data) {
    commit(SetUserData, data);
  },

  async [FetchUserData]({ commit }) {
    try {
      const { data } = await _arcaneApiGateway.get(apiGatewayEndpoints.user.profile);
      commit(SetUserData, data);
      // The route guard also calls this, but only on navigation — this call
      // covers the case where the profile response (org-feature-derived
      // roles included) resolves after the user has already landed on a page.
      identifyUser(data.email, data.roles);

      return data;
    } catch (error) {
      console.error('Error in fetching userData', error);
    }
  },

  async [UpdateUserData]({ state, commit }, data) {
    try {
      await _arcaneApiGateway.patch(apiGatewayEndpoints.user.profile, data);

      commit(SetUserData, { ...state.userData, ...data });
    } catch (error) {
      console.error(error);
    }
  },

  async [SignUp]({ commit }, { email, password }) {
    try {
      commit(ChangeEmail, email);

      const { data } = await ArcaneApi.getInstance().post(endpoints.auth.signUp, { email, password });

      state.userData = data;
    } catch (error) {
      console.error(error);
    }
  },

  async [SendResetPassword]({ state }) {
    try {
      const res = await ArcaneApi.getInstance().post(endpoints.auth.resetPassword, { email: state.email });

      return res;
    } catch (error) {
      console.error(error);
    }
  },

  async [ChangePassword](_, data) {
    try {
      await _arcaneApiGateway.post(apiGatewayEndpoints.user.changePassword, data);
    } catch (error) {
      console.error(error);
    }
  },

  async [Logout]({ dispatch }) {
    localStorage.clear();
    resetUser();
    dispatch(ClearStore);
    try {
      await auth0Client.logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
      // Redirect logic here
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  /**
   * ORGANIZATION
   */

  async [FetchOrganization]() {
    try {
      const { data } = await ArcaneApi.getInstance().get(endpoints.organization.base);

      return data;
    } catch (error) {
      console.error(error);
    }
  },

  async [UpdateOrganization]({ commit, state }, newData) {
    try {
      const { data = {} } = await ArcaneApi.getInstance().patch(endpoints.organization.base, newData);
      const index = state.userData?.organizations?.findIndex((organization) => organization.id === data?.id);

      if (index >= 0) {
        const organizations = [...state.userData.organizations];
        organizations[index] = { ...organizations[index], ...data };

        commit(SetUserData, { ...state.userData, organizations });
      }

      return data || {};
    } catch (error) {
      console.error(error);
    }
  },

  [SetActiveOrganization]({ commit }, data) {
    commit(SetActiveOrganization, data);
  },

  [SetLastOrganizationId]({ commit }, organizationId) {
    commit(SetLastOrganizationId, organizationId);
  },

  [OrgEnsured]({ commit }) {
    commit(SetOrgEnsuredState, true);
  },

  async [ChangeActiveOrganization]({ commit }, { organizationId, auth0Id }) {
    commit(SetActiveOrganization, organizationId);
    commit(SetLastOrganizationId, organizationId);

    if (auth0Id) {
      // Setting field to local storage so the user is redirected to the current page again
      // TODO we should not use local storage and uni_redirect should be taken into consideration whenloginWithRedirect
      localStorage.setItem('auth.redirect', window.location.pathname);
      try {
        await auth0Client.loginWithRedirect({
          authorizationParams: {
            prompt: 'none',
            organization: auth0Id,
          },
        });
      } catch (error) {
        console.error(error);
        // TODO redirect to error page
      }
    }
  },
};

const getters = {
  [GetUserData]: (state) => state.userData,

  [GetOrganizationId]: (state) => state.organizationId,

  [GetOrganizationName]: (state) => state.organizationName,

  [GetLastOrganizationId]: (state) => (userId) => state.lastOrgId?.[userId],

  [GetActiveOrganization]: (state) =>
    state.userData?.orgId || state.userData?.organizations?.find((org) => org.id === state.organizationId),

  [HasUnlimitedAccess]: (state) => {
    return !state.planInfo?.config || isEmpty(state.planInfo?.config);
  },

  [GetActiveOrganizationRole]: (state) => (roleName) =>
    state.userData?.organizations
      .find((org) => org.id === state.organizationId)
      ?.roles.find((role) => role.name === roleName),

  [GetOrganizations]: (state) => state.userData?.organizations ?? [],

  [GetOrganizationById]: (state) => (orgId) => state.userData?.organizations.find((org) => org.id === orgId),

  [GetMainSubscription]: (state) => state.subscriptions?.[0] || {},
};

export default {
  namespaced: true, // Important for namespacing module
  state,
  mutations,
  actions,
  getters,
};
